import { createBrowserRouter, RouterProvider } from "react-router"

import {
  AcceptInvitePage,
  ForgotPasswordPage,
  LoginPage,
  ResetPasswordPage,
} from "@/features/auth"
import { ClientsPage } from "@/features/clients"
import { QueuePage } from "@/features/queue"
import { ReviewPage } from "@/features/review"
import { UsersKeysPage } from "@/features/users-keys"
import { PrimitivesIndexRoute } from "@/routes/dev/primitives"
import { AlertPreviewRoute } from "@/routes/dev/primitives/alert"
import { AvatarPreviewRoute } from "@/routes/dev/primitives/avatar"
import { ButtonPreviewRoute } from "@/routes/dev/primitives/button"
import { ComboboxPreviewRoute } from "@/routes/dev/primitives/combobox"
import { DataTablePreviewRoute } from "@/routes/dev/primitives/data-table"
import { DatePickerPreviewRoute } from "@/routes/dev/primitives/date-picker"
import { DialogPreviewRoute } from "@/routes/dev/primitives/dialog"
import { EmptyStatePreviewRoute } from "@/routes/dev/primitives/empty-state"
import { FormFieldPreviewRoute } from "@/routes/dev/primitives/form-field"
import { IconButtonPreviewRoute } from "@/routes/dev/primitives/icon-button"
import { InputPreviewRoute } from "@/routes/dev/primitives/input"
import { PaginationPreviewRoute } from "@/routes/dev/primitives/pagination"
import { PopoverPreviewRoute } from "@/routes/dev/primitives/popover"
import { SearchInputPreviewRoute } from "@/routes/dev/primitives/search-input"
import { SelectPreviewRoute } from "@/routes/dev/primitives/select"
import { SheetPreviewRoute } from "@/routes/dev/primitives/sheet"
import { StatCardPreviewRoute } from "@/routes/dev/primitives/stat-card"
import { TableSortPreviewRoute } from "@/routes/dev/primitives/table-sort"
import { TextareaPreviewRoute } from "@/routes/dev/primitives/textarea"
import { ToastPreviewRoute } from "@/routes/dev/primitives/toast"
import { VersionTimelinePreviewRoute } from "@/routes/dev/primitives/version-timeline"
import { HelloRoute } from "@/routes/hello"
import { NavPlaceholderRoute } from "@/routes/nav-placeholder"

import { ErrorBoundary } from "./error-boundary"
import { ShellRoute } from "./shell-route"

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <ErrorBoundary>
        <LoginPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/accept-invite",
    element: (
      <ErrorBoundary>
        <AcceptInvitePage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <ErrorBoundary>
        <ForgotPasswordPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <ErrorBoundary>
        <ResetPasswordPage />
      </ErrorBoundary>
    ),
  },
  {
    element: (
      <ErrorBoundary>
        <ShellRoute />
      </ErrorBoundary>
    ),
    children: [
      { path: "/", element: <HelloRoute /> },
      { path: "/reviews", element: <QueuePage /> },
      { path: "/reviews/:id", element: <ReviewPage /> },
      { path: "/reviews/:id/:version", element: <ReviewPage /> },

      { path: "/admin/clients", element: <ClientsPage /> },
      { path: "/admin/users", element: <UsersKeysPage /> },
      { path: "/admin/rules", element: <NavPlaceholderRoute /> },

      { path: "/tools/label-finder", element: <NavPlaceholderRoute /> },
      { path: "/tools/doc-compare", element: <NavPlaceholderRoute /> },
      { path: "/tools/field-review", element: <NavPlaceholderRoute /> },

      { path: "/dev/dataset", element: <NavPlaceholderRoute /> },
      { path: "/dev/openapi", element: <NavPlaceholderRoute /> },
      { path: "/dev/primitives", element: <PrimitivesIndexRoute /> },
      { path: "/dev/primitives/alert", element: <AlertPreviewRoute /> },
      { path: "/dev/primitives/avatar", element: <AvatarPreviewRoute /> },
      { path: "/dev/primitives/button", element: <ButtonPreviewRoute /> },
      { path: "/dev/primitives/combobox", element: <ComboboxPreviewRoute /> },
      { path: "/dev/primitives/data-table", element: <DataTablePreviewRoute /> },
      { path: "/dev/primitives/date-picker", element: <DatePickerPreviewRoute /> },
      { path: "/dev/primitives/dialog", element: <DialogPreviewRoute /> },
      { path: "/dev/primitives/empty-state", element: <EmptyStatePreviewRoute /> },
      { path: "/dev/primitives/form-field", element: <FormFieldPreviewRoute /> },
      { path: "/dev/primitives/icon-button", element: <IconButtonPreviewRoute /> },
      { path: "/dev/primitives/input", element: <InputPreviewRoute /> },
      { path: "/dev/primitives/pagination", element: <PaginationPreviewRoute /> },
      { path: "/dev/primitives/popover", element: <PopoverPreviewRoute /> },
      { path: "/dev/primitives/search-input", element: <SearchInputPreviewRoute /> },
      { path: "/dev/primitives/select", element: <SelectPreviewRoute /> },
      { path: "/dev/primitives/sheet", element: <SheetPreviewRoute /> },
      { path: "/dev/primitives/stat-card", element: <StatCardPreviewRoute /> },
      { path: "/dev/primitives/table-sort", element: <TableSortPreviewRoute /> },
      { path: "/dev/primitives/textarea", element: <TextareaPreviewRoute /> },
      { path: "/dev/primitives/toast", element: <ToastPreviewRoute /> },
      { path: "/dev/primitives/version-timeline", element: <VersionTimelinePreviewRoute /> },
    ],
  },
])

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />
}
